import { useMemo } from 'react';
import { isEmpty } from 'validate.js';

import { LITTER_DISPLAY_STATES } from 'dpl/shared/constants/shared';
import useResourceQuery from 'dpl/shared/fetching/hooks/useResourceQuery';
import {
  littersResourceDefinition,
  singleLitterResourceDefinition
} from 'dpl/shared/utils/tptConnectResourceDefinitions';

interface UseAvailableLittersArgs {
  breederProfileId: number;
  selectedLitterId: number | null;
  selectedDogId: number | null;
}

export default function useAvailableLitters({
  breederProfileId,
  selectedLitterId,
  selectedDogId
}: UseAvailableLittersArgs) {
  const {
    isLoading: isLoadingBornLitters,
    value: { data: bornLitters }
  } = useResourceQuery(
    littersResourceDefinition(breederProfileId, {
      status: LITTER_DISPLAY_STATES.CURRENT
    })
  );

  const isMissingSelectedLitterData = useMemo(() => {
    if (!selectedLitterId || isLoadingBornLitters) {
      return false;
    }

    return !bornLitters.some((litter: any) => litter.id === selectedLitterId);
  }, [isLoadingBornLitters, bornLitters, selectedLitterId]);

  const {
    isLoading: isLoadingLitterSelectionData,
    value: { data: litterSelectionData }
  } = useResourceQuery({
    ...singleLitterResourceDefinition(breederProfileId, selectedLitterId),
    enabled: isMissingSelectedLitterData
  });

  const isLoading = isLoadingBornLitters || isLoadingLitterSelectionData;

  const litters = useMemo(() => {
    if (isLoading) return [];

    const allLitters = litterSelectionData?.id
      ? [litterSelectionData, ...bornLitters]
      : bornLitters;

    const availableLitters: any[] = [];

    for (let index = 0; index < allLitters.length; index++) {
      const litter = allLitters[index];

      const puppies = litter.puppies.filter((puppy: any) => {
        if (puppy.id === selectedDogId) {
          // Always include selected puppy
          return true;
        }

        return puppy.available && !puppy.snoozed;
      });

      if (!isEmpty(puppies)) {
        availableLitters.push({ ...litter, puppies });
      }
    }

    return availableLitters;
  }, [isLoading, litterSelectionData, bornLitters, selectedDogId]);

  return { isLoading, litters };
}
